import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { TUTORIALS_ROUTE } from '../../constants/routePathConstants';
import { useUserContext } from '../../contexts/UserContext';
import { apiGetTutorialRooms, apiUpdateUserDetails } from '../../helpers/api';
import { objectToFormData } from '../../helpers/jsHelper';
import { isCuratorPage } from '../../helpers/urlHelper';
import {
  GAMIFIED_STEPS,
  gamifyBoardingActions,
  selectGamifiedCurrentStep,
  selectIsGamifiedDialogOpen,
} from '../../redux/slicers/gamifySlicer';
import Dialog from '../dialog/dialog';
import { showHideTooltip } from '../gamificationTooltip/helpers';
import { StepZero, StepOne, StepFive } from './steps';
import { Mixpanel, MxEvent } from '../../common/Mixpanel';

export const CURATOR_ROOM_URL = '/curator/room/:id';

const GamifyOnboardingSteps = ({ rooms }) => {
  const dispatch = useDispatch();
  const { currentUser } = useUserContext();
  const firstName = currentUser?.user?.first_name;
  const currentStep = useSelector(selectGamifiedCurrentStep);
  const setStep = (step) => dispatch(gamifyBoardingActions.setGamifiedCurrentStep(step));

  const renderStep = useCallback(
    (step) => {
      if (step === GAMIFIED_STEPS.LETS_START) {
        return <StepZero name={firstName} setStep={setStep} />;
      }
      if (step === GAMIFIED_STEPS.CHOOSE_ROOM) {
        return <StepOne setStep={setStep} rooms={rooms} />;
      }
      if (step === GAMIFIED_STEPS.SAVE) {
        return <StepFive name={firstName} setStep={setStep} />;
      }
      return <StepZero name={firstName} setStep={setStep} />;
    },
    [currentStep, rooms, firstName]
  );
  return renderStep(currentStep);
};

function GamifyOnBoarding() {
  const dispatch = useDispatch();
  const location = useLocation();
  const isDialogOpen = useSelector(selectIsGamifiedDialogOpen);
  const currentStep = useSelector(selectGamifiedCurrentStep);
  const { currentUser, updateUserData } = useUserContext();
  const [rooms, setRooms] = useState([]);
  const hideOnHide = currentStep === GAMIFIED_STEPS.SAVE;

  const closeOnboardingModel = () => {
    const obj = { gamifyOnBoarding: { show: false }};
    showHideTooltip(currentUser, updateUserData, obj);
  }

  useEffect(() => {
    if (!currentUser || isCuratorPage()) return;
    const other_info = currentUser?.user?.other_info;
    dispatch(gamifyBoardingActions.setGamifiedCurrentStep(other_info?.gamifyOnBoarding?.step));
    dispatch(gamifyBoardingActions.openGamifiedDialog(other_info?.gamifyOnBoarding?.show));
    return () => closeOnboardingModel();
  }, []);

  useEffect(async () => {
    if (!currentUser || !isDialogOpen) return;
    if (currentStep === GAMIFIED_STEPS.LETS_START || currentStep === GAMIFIED_STEPS.CHOOSE_ROOM) {
      const allRooms = await apiGetTutorialRooms();
      if (allRooms && allRooms.results.length) {
        const enabledRoom = allRooms.results.filter((a) => a.is_enabled);
        setRooms(enabledRoom);
      }
    }
  }, [currentStep, isDialogOpen]);

  useEffect(async () => {
    if (
      !isDialogOpen ||
      !currentUser ||
      isCuratorPage() ||
      currentStep === GAMIFIED_STEPS.LETS_START ||
      currentStep === GAMIFIED_STEPS.SAVE ||
      currentStep === GAMIFIED_STEPS.PRODUCT
    )
      return;
    const other_info = currentUser?.user?.other_info;
    const payload = {
      other_info: JSON.stringify({
        ...other_info,
        gamifyOnBoarding: {
          ...other_info?.gamifyOnBoarding,
          step: currentStep,
          show: isDialogOpen,
        },
      }),
    };
    const formData = objectToFormData(payload);
    const res = await apiUpdateUserDetails(formData);
    if (res) {
      updateUserData({
        ...currentUser,
        user: {
          ...currentUser?.user,
          other_info: {
            ...other_info,
            ...res?.other_info,
          },
        },
      });
    }
  }, [currentStep, isDialogOpen]);

  const onClose = () => {
    Mixpanel.track( true, MxEvent.GAMIFY_ONBOARDING_SKIPPED,{currentStep: currentStep});
    let showTooltip = false;
    if ('other_info' in currentUser?.user) {
      if ('showTutorialsTooltip' in currentUser?.user?.other_info) {
        showTooltip = true;
      } else {
        showTooltip = false;
      }
    } else {
      showTooltip = false;
    }
    const isBeginning =
      !currentStep ||
      currentStep === GAMIFIED_STEPS.LETS_START ||
      currentStep === GAMIFIED_STEPS.CHOOSE_ROOM;
    if (!location.pathname.includes(TUTORIALS_ROUTE) && !showTooltip && isBeginning) {
      const obj = { showTutorialsTooltip: true };
      showHideTooltip(currentUser, updateUserData, obj);
    } else {
      closeOnboardingModel();
    }
    dispatch(gamifyBoardingActions.closeGamifiedDialog());
  };

  if (!isDialogOpen || isCuratorPage()) return null;

  return (
    <Dialog {...(!hideOnHide && { onHide: () => onClose() })} size="lg">
      <GamifyOnboardingSteps rooms={rooms} />
    </Dialog>
  );
}

export default GamifyOnBoarding;
